/**
 * Screens whose code is downloaded on demand, and what to do when that download fails (RW-412).
 *
 * `React.lazy(() => import('./step'))` puts the step in its own file, fetched the first time it
 * renders. If that fetch fails, the bundler's loader (Turbopack) tries once more after 200-600 ms,
 * then throws `ChunkLoadError`. React.lazy keeps that rejection for the life of the page, by design,
 * so the error reaches the ErrorBoundary and the visitor is stuck on the crash screen. On
 * /getting-started that ended a sign-up on its last step (28/10/2026, session
 * 248a6150-3ec6-48ce-a971-7b316aabb674); since 20/09/2026 it hit about one employer session a week.
 *
 * A second `import()` in the same page does download again: the Turbopack runtime deletes its cache
 * entry for a file that failed (read in the production build of 6b50fa6, `turbopack-1-*.js`), and
 * webpack does the same. So retrying inside the function given to React.lazy recovers without a
 * reload. In the crash sessions measured, the visitors who reloaded on their own got through.
 *
 * Why the download fails is still unknown: in every crash the phone was online (other requests
 * succeeded within 3 seconds) and the file existed. The browser hides the HTTP status of these
 * no-cors script loads, so each failure is also probed with a CORS `fetch` of the same URL (the
 * asset host answers `access-control-allow-origin: *`), and the result is logged.
 */

/** Waits before the 2nd, 3rd and 4th attempt. About 7 s in total, on top of the loader's own retry. */
const RETRY_DELAYS_MS = [1000, 2000, 4000];

/** The probe gives up after this long, so a hanging request still produces a log line. */
const PROBE_TIMEOUT_MS = 10000;

/**
 * True for a failed download of on-demand code. Turbopack and webpack name the error
 * `ChunkLoadError`; the message patterns cover builds that don't (CSS chunks, native `import()`).
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return (
    error.name === 'ChunkLoadError' ||
    /Failed to load chunk|Loading (CSS )?chunk \S+ failed|Failed to fetch dynamically imported module/.test(
      error.message
    )
  );
}

/**
 * - `retrying`: an attempt failed, another one follows.
 * - `gave_up`: the last attempt failed, the error goes on to the ErrorBoundary.
 * - `recovered`: an attempt after a failure succeeded, so the visitor never saw an error.
 * - `auto_reload`: the ErrorBoundary is reloading the page once.
 */
export type ChunkLoadOutcome = 'retrying' | 'gave_up' | 'recovered' | 'auto_reload';

/**
 * Log one chunk download failure, or its recovery, to the O2 logs stream (`_rumlog`,
 * `event = 'chunk_load_failure'`), joinable to the RUM session on `session_id`.
 *
 * For `retrying` and `gave_up` it first probes the failed URL with a CORS `fetch` that skips the
 * browser cache, and records what the network answered: `probe_status` (and size, type, time), or
 * `probe_error` when the request itself failed. Goes through the logs SDK, not a RUM action,
 * because RUM actions wait for the Measurement consent and /getting-started shows no banner.
 *
 * Never throws, and resolves once the log is queued.
 */
export async function reportChunkLoadFailure(
  error: unknown,
  details: { outcome: ChunkLoadOutcome; attempt?: number; elapsedMs?: number }
): Promise<void> {
  if (typeof window === 'undefined') return;

  try {
    const message = error instanceof Error ? error.message : String(error);
    // Deployed builds name the chunk by its full URL (the per-deployment asset host); local builds
    // have no asset prefix and name it by a path, which is resolved against the page.
    const chunkPath = message.match(/(?:https?:\/\/\S+?|\/_next\/\S+?)\.(?:js|css)\b/)?.[0];
    const chunkUrl = chunkPath ? new URL(chunkPath, window.location.href).href : undefined;
    const connection = (navigator as Navigator & { connection?: { effectiveType?: string; rtt?: number } }).connection;

    const probe: Record<string, unknown> = {};
    if (chunkUrl && (details.outcome === 'retrying' || details.outcome === 'gave_up')) {
      const startedAt = performance.now();
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
      try {
        const response = await fetch(chunkUrl, { mode: 'cors', cache: 'no-store', signal: controller.signal });
        const body = await response.text();
        probe.probe_status = response.status;
        probe.probe_content_type = response.headers.get('content-type');
        probe.probe_bytes = body.length;
      } catch (probeError) {
        probe.probe_error =
          probeError instanceof Error ? `${probeError.name}: ${probeError.message}` : String(probeError);
      } finally {
        window.clearTimeout(timeout);
        probe.probe_ms = Math.round(performance.now() - startedAt);
      }
    }

    const { openobserveLogs } = await import('@openobserve/browser-logs');
    openobserveLogs.logger.warn('chunk_load_failure', {
      event: 'chunk_load_failure',
      outcome: details.outcome,
      attempt: details.attempt,
      elapsed_ms: details.elapsedMs,
      chunk_url: chunkUrl,
      chunk_host: chunkUrl ? new URL(chunkUrl).host : undefined,
      error_message: message.slice(0, 300),
      online: navigator.onLine,
      visibility: document.visibilityState,
      connection_type: connection?.effectiveType,
      connection_rtt: connection?.rtt,
      ...probe,
    });
  } catch {
    // Telemetry about a failure must never become a second failure.
  }
}

/**
 * Wrap the function given to `React.lazy` so a failed download is retried in place, up to 3 more
 * times (after 1, 2 and 4 s), before the error reaches the ErrorBoundary. Any other error is
 * rethrown at once. Every failure and every recovery is logged with `reportChunkLoadFailure`.
 *
 *   const Role = React.lazy(withChunkLoadRetry(() => import('./steps/role')));
 */
export function withChunkLoadRetry<T>(load: () => Promise<T>): () => Promise<T> {
  return async () => {
    const startedAt = Date.now();
    let lastError: unknown;

    for (let attempt = 1; ; attempt += 1) {
      try {
        const loaded = await load();
        if (lastError) {
          void reportChunkLoadFailure(lastError, { outcome: 'recovered', attempt, elapsedMs: Date.now() - startedAt });
        }
        return loaded;
      } catch (error) {
        if (!isChunkLoadError(error)) throw error;
        lastError = error;
        const isLastAttempt = attempt > RETRY_DELAYS_MS.length;
        void reportChunkLoadFailure(error, {
          outcome: isLastAttempt ? 'gave_up' : 'retrying',
          attempt,
          elapsedMs: Date.now() - startedAt,
        });
        if (isLastAttempt) throw error;
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAYS_MS[attempt - 1]));
      }
    }
  };
}
